import React, { createContext, useContext, useCallback, useState } from 'react';
import { hasValue, isFunction, sleep as delay } from 'swipeclock-frontend';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import MuiAlert from '@mui/material/Alert';
import MuiSnackbar from '@mui/material/Snackbar';
import PropTypes from 'prop-types';

export const SetSnackbarContext = createContext();

export function useSetSnackbarContext() {
  return useContext(SetSnackbarContext);
}

export const ShowSnackbarContext = createContext();

export function useShowSnackbarContext() {
  return useContext(ShowSnackbarContext);
}

export default function SnackbarProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [snackbar, _setSnackbar] = useState({});

  const setSnackbarOpen = useCallback((isOpen) => setOpen(isOpen), [setOpen]);

  const setSnackbar = useCallback((element) => _setSnackbar(element), []);

  const handleClose = async () => {
    if (isFunction(snackbar?.onClose)) {
      snackbar?.onClose();
    }
    setSnackbarOpen(false);
    await delay(420);
    setSnackbar(null);
  };

  return (
    <ShowSnackbarContext.Provider value={setSnackbarOpen}>
      <SetSnackbarContext.Provider value={setSnackbar}>
        <>
          {children}
          {snackbar && (
            <Snackbar onClose={handleClose} open={open} snackbar={snackbar} />
          )}
        </>
      </SetSnackbarContext.Provider>
    </ShowSnackbarContext.Provider>
  );
}

SnackbarProvider.propTypes = {
  children: PropTypes.any,
};

function Snackbar({ onClose: handleClose, open, snackbar }) {
  const { sx, content, message, key, action, severity } = snackbar ?? {
    sx: {},
  };

  const closeAction = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  const anchor = snackbar?.anchor
    ? snackbar.anchor
    : { vertical: 'bottom', horizontal: 'left' };

  const hide = snackbar.hide !== undefined ? snackbar.hide : 4000;

  return (
    <>
      {severity && (
        <MuiSnackbar
          onClose={handleClose}
          anchorOrigin={anchor}
          autoHideDuration={hide}
          key={key}
          open={open}
        >
          <Alert
            action={action ?? closeAction}
            severity={severity}
            sx={{ width: '100%', ...sx }}
          >
            {hasValue(content) ? content : message}
          </Alert>
        </MuiSnackbar>
      )}
      {!severity && (
        <MuiSnackbar
          action={
            <>
              {action}
              {closeAction}
            </>
          }
          anchorOrigin={anchor}
          autoHideDuration={hide}
          key={key}
          message={message}
          onClose={handleClose}
          open={open}
          sx={sx}
        />
      )}
    </>
  );
}

Snackbar.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  snackbar: PropTypes.object,
};

/* eslint-disable prefer-arrow-callback */
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export function useSnackbarForMessage() {
  // Note on the options object:
  // Pass all these: sx, content, message, key, action,
  // via the options object and then we spread it out to make it work
  // as expected without refactoring all the code already in the project

  const setSnackbar = useSetSnackbarContext();
  const showSnackbar = useShowSnackbarContext();

  return {
    setSnackbar: (message, severity = 'success', options = {}) => {
      const snackbar = {
        message,
        severity,
        ...options,
      };
      setSnackbar(snackbar);
    },
    showSnackbar: () => showSnackbar(true),
    // The settings and showing in one function --- magic!
    announceSnackbar: (message, severity = 'success', options = {}) => {
      const snackbar = {
        message,
        severity,
        key: options?.key ?? `${severity}-${message}}`,
        ...options,
      };
      setSnackbar(snackbar);
      showSnackbar(true);
    },
  };
}
